// FirstRun.tsx — Bot Overview's "brand new" layout (Task 9, handoff README §3 "Bot Overview —
// brand new (the 'three zeros')"), rendered instead of the populated screen (Task 8) whenever
// `overview.everHadConversation === false`. Selected in `BotOverview.tsx` on that explicit
// boolean, never on a 14-day count — see the comment at that call site for why.
//
// The rule this whole screen exists to fix: a zero reads as failure, a dash reads as not yet.
// The three stat cards below keep the exact grid/size the populated screen uses (`2fr 1fr 1fr`,
// gap 16) so the page never jumps once the first conversation lands and Task 8's layout takes
// over — only their treatment (dashed, transparent, em dash) differs.
//
// This is a full self-contained screen, not a swapped-in fragment of the populated one: the
// header here has its own subline ("Created X ago", not "Live on <domain> · answering since
// <date>") and no header actions, matching `Dashboard Shell.dc.html`'s "Overview · brand new"
// screen exactly — that screen renders no `Test in Playground`/`Add sources` buttons up top
// because the embed snippet below is the one thing left to do, not a header action.
//
// DEPARTS FROM THE HANDOFF at the `empty` knowledge state (Task 1, dashboard-shell-feedback
// plan, 2026-08-09). README §3 keeps the embed snippet as the hero's primary action
// unconditionally — "most owners have not pasted it yet" — and only proposes swapping to the
// populated layout once the widget has actually been seen on the customer's domain. That
// argument holds once the bot has something to answer from; at zero sources it doesn't: pasting
// the snippet wires up a widget that can only refuse. Dev testing found this reads as "nothing
// left to do" right after creating a bot, with the actual next step (add a source) demoted to a
// small link inside the Knowledge card. `empty` now leads with "Add a source" as the hero's
// primary action and as step 1 of "What happens next"; `indexing`/`ready` are unchanged and
// still lead with the snippet, matching the handoff. No animation, no dismissible coach mark —
// Johnny explicitly rejected both; the fix is content and ordering, not decoration.
import { Link } from 'react-router';
import { Button } from '@repo/ui/components/button';
import { StatusChip } from '@repo/ui/components/status-chip';
import type { BotDto, BotOverviewDto } from '@repo/shared-types';
import { EmbedSnippet } from '../EmbedSnippet';
import { PageHeader } from '../PageHeader';
import { mailtoSnippetLink } from '../../lib/embed-snippet';
import { landingUrl } from '../../lib/landing-url';
import { firstRunKnowledgeState, knowledgeChipView } from '../../lib/overview';
import { relativeTime } from '../../lib/relative-time';
import { useT } from '../../locales/context';
import type { Catalog } from '../../locales/en';

/** The three dashed stat cards' label/hint pairs — a function of `t` (not a module constant,
 * since Task 7 moved every literal here into the catalog) so it can only be built inside a
 * component render, same as `heroByState`/`nextStepsFor` below. */
function zeroStats(t: Catalog): { label: string; hint: string }[] {
  return [
    { label: t.overview.messagesLabel, hint: t.overview.zeroStatMessagesHint },
    { label: t.overview.leadsLabel, hint: t.overview.zeroStatLeadsHint },
    { label: t.overview.unansweredLabel, hint: t.overview.zeroStatUnansweredHint },
  ];
}

// Fix round 2 — the headline/icon used to stay "Your bot is ready to answer" plus a green check
// across all three knowledge states, which overclaimed louder than the body paragraph ever did
// (a skimming owner reads the headline and the tick, not the sentence below it). Reserves the
// green check for 'ready'; 'indexing'/'empty' get a neutral dot in a token already used
// elsewhere for those exact states (`--st-indexing`/`--st-pending`, the same vocabulary
// `StatusChip` uses — no new colour). Keyed by `firstRunKnowledgeState`'s own three states, not a
// fourth "error" bucket: the Knowledge card's chip already surfaces a failed source on its own,
// and the review scoped this to the same three states already derived.
//
// A function of `t` (Task 7), not a module constant — its headlines moved into the catalog.
function heroByState(
  t: Catalog,
): Record<'empty' | 'indexing' | 'ready', { headline: string; token: string; animated: boolean }> {
  return {
    ready: { headline: t.overview.heroReadyHeadline, token: '--st-ready', animated: false },
    indexing: { headline: t.overview.heroIndexingHeadline, token: '--st-indexing', animated: true },
    empty: { headline: t.overview.heroEmptyHeadline, token: '--st-pending', animated: false },
  };
}

/** "What happens next"'s three step cards, as a function of `t` and `knowledgeState` (Task 7 —
 * both variants' titles/bodies moved into the catalog). See the comment this replaces (still
 * true of the logic, just no longer of literal module constants) for why the `empty` sequence
 * substitutes its own first two steps rather than growing to four cards: the real next step at
 * `empty` is adding a source, not a visitor arriving at a snippet that isn't pasted yet and
 * wouldn't have anything to answer from if it were, and step 2's body ("So you read exactly what
 * it said…") reuses the SAME `stepEmailAnswerBody` key in both sequences — the sentence doesn't
 * change, only which title it sits under. Step 3 ("What it couldn't answer lands here") is
 * identical in both sequences — verbatim (README §3) the moment a new owner first learns the
 * product's actual differentiator, with nothing else on screen competing for attention. */
function nextStepsFor(
  t: Catalog,
  knowledgeState: 'empty' | 'indexing' | 'ready',
): { n: string; title: string; body: string }[] {
  const stepUnanswered = { n: '3', title: t.overview.stepUnansweredTitle, body: t.overview.stepUnansweredBody };
  if (knowledgeState !== 'empty') {
    return [
      { n: '1', title: t.overview.stepVisitorAsksTitle, body: t.overview.stepVisitorAsksBody },
      { n: '2', title: t.overview.stepEmailAnswerTitle, body: t.overview.stepEmailAnswerBody },
      stepUnanswered,
    ];
  }
  return [
    { n: '1', title: t.overview.stepAddSourceTitle, body: t.overview.stepAddSourceBody },
    { n: '2', title: t.overview.stepAddSourceVisitorTitle, body: t.overview.stepEmailAnswerBody },
    stepUnanswered,
  ];
}

export function FirstRun({
  botId,
  bot,
  overview,
  domain,
}: {
  botId: string | undefined;
  bot: BotDto;
  overview: BotOverviewDto;
  /** The bot's first `url` source's hostname, derived the same way `BotOverview.tsx`'s header
   * subline derives it for the populated screen — `null` when there isn't one (a text/PDF-only
   * bot), in which case the hero sentence below drops the domain clause rather than inventing a
   * placeholder. */
  domain: string | null;
}) {
  const t = useT();
  const { knowledge } = overview;
  // Fix-wave item 5: same chip-state/label derivation `BotOverview.tsx`'s populated Knowledge
  // card uses — one shared `knowledgeChipView` (`lib/overview.ts`) instead of two byte-identical
  // ternary chains.
  const knowledgeChip = knowledgeChipView(knowledge, t);
  // Fix round 1 — the hero's "It read N pages…" sentence must not overclaim while a source is
  // still crawling/indexing (the most common moment this screen renders, right after
  // onboarding, is exactly when `pageCount` can still be 0). See `firstRunKnowledgeState`'s own
  // comment for why 'empty' and 'indexing' are distinct facts.
  const knowledgeState = firstRunKnowledgeState(knowledge);
  const domainMono = domain ? <span className="font-mono text-[13px] text-foreground">{domain}</span> : null;
  const hero = heroByState(t)[knowledgeState];
  const nextSteps = nextStepsFor(t, knowledgeState);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t.overview.title}
        subtitle={t.overview.createdSubtitle({ time: relativeTime(bot.createdAt, t.time) })}
      />

      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-[2fr_1fr]">
        <div
          // Same card — position, size, radius, padding — across all three states (fix round 1's
          // stability rule for the counter cards applies here too, for the same reason: nothing
          // should jump when a bot finishes indexing and this flips to 'ready'). Only the token
          // driving the border/wash and the icon/headline below it change.
          className="rounded-[14px] border p-5 pb-[18px] shadow-el-1"
          style={{
            borderColor: `color-mix(in oklch, var(${hero.token}) 38%, var(--border))`,
            backgroundColor: `color-mix(in oklch, var(${hero.token}) 6%, var(--card))`,
          }}
        >
          <div className="flex items-center gap-2.5">
            <span
              aria-hidden="true"
              className="grid h-7 w-7 shrink-0 place-items-center rounded-full"
              style={{ color: `var(${hero.token})`, backgroundColor: `color-mix(in oklch, var(${hero.token}) 22%, transparent)` }}
            >
              {knowledgeState === 'ready' ? (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              ) : (
                // Not-done-yet states get the same dot `StatusChip` uses for these exact
                // statuses (`st-dot-active` bounces for 'indexing', matching its `ANIMATED` set;
                // 'empty'/pending sits still) — the product's one existing status vocabulary,
                // not a new icon.
                <span
                  className={`size-3 rounded-full ${hero.animated ? 'st-dot-active' : ''}`}
                  style={{ backgroundColor: `var(${hero.token})` }}
                />
              )}
            </span>
            <h2 className="font-display text-[22px] font-bold tracking-[-0.015em]">{hero.headline}</h2>
          </div>
          <p className="mt-2.5 max-w-[62ch] text-sm text-pretty text-muted-foreground">
            {knowledgeState === 'empty' ? (
              <>{t.overview.heroEmptyBody}</>
            ) : knowledgeState === 'indexing' ? (
              knowledge.worstStatus === 'error' ? (
                // Fix round 3 — "check back in a few minutes" is false for a source that has
                // already failed; nothing is going to finish on its own. Same calm 'indexing'
                // headline/icon bucket (round 2 stands), but the paragraph has to point at the
                // real next action instead of promising a wait that never resolves.
                <>{t.overview.heroIndexingErrorBody}</>
              ) : (
                <>
                  {t.overview.heroIndexingPrefix}
                  {domainMono ? <> {domainMono}</> : ` ${t.overview.heroIndexingNoSourceFallback}`}
                  {t.overview.heroIndexingSuffix}
                </>
              )
            ) : (
              <>
                {t.overview.heroReadyBodyPrefix({ n: knowledge.pageCount })}
                {domainMono ? (
                  <>
                    {t.overview.heroReadyFromDomain}
                    {domainMono}
                  </>
                ) : null}
                {t.overview.heroReadyBodySuffix}
              </>
            )}
          </p>

          <div className="mt-[18px]">
            {knowledgeState === 'empty' ? (
              // The primary action here is adding a source, not the embed snippet (file header's
              // departure note) — the snippet has nothing to prove out until then.
              <>
                <Button asChild className="h-10 px-5 text-[13.5px]">
                  <Link to={`/bots/${botId}/sources`}>{t.overview.addSourceCta}</Link>
                </Button>
                <p className="mt-2.5 max-w-[46ch] text-[12.5px] text-muted-foreground">
                  {t.overview.addSourceHelperText}
                </p>
              </>
            ) : (
              <>
                <div className="mb-[7px] font-mono text-[10.5px] tracking-[0.09em] text-muted-foreground">
                  {t.overview.pasteBeforeBodyLabel}
                </div>
                <EmbedSnippet publicId={bot.publicId} variant="hero" />
                <div className="mt-2.5 flex items-center gap-4">
                  <a
                    href={mailtoSnippetLink(bot.name, bot.publicId, t)}
                    className="text-[12.5px] font-medium text-primary hover:underline"
                  >
                    {t.overview.emailSnippetLink}
                  </a>
                  <span className="text-[12.5px] text-muted-foreground">·</span>
                  <a
                    href={`${landingUrl()}/docs/install`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[12.5px] font-medium text-primary hover:underline"
                  >
                    {t.onboarding.goLiveInstallGuidesLink}
                  </a>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-col rounded-[14px] border bg-card p-5 shadow-el-1">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-display text-lg font-semibold">{t.overview.knowledgeHeading}</h2>
            {knowledge.sourceCount > 0 && (
              <StatusChip state={knowledgeChip.state} label={knowledgeChip.label} />
            )}
          </div>

          {/* Same zero-source fallback `BotOverview.tsx`'s populated Knowledge card uses — a
              brand-new bot with no sources yet has nothing to report rows for, and a
              "Sources syncing" chip above an empty table would be as much of an overclaim as
              the hero's old ungated sentence was. */}
          {knowledge.sourceCount === 0 ? (
            <div className="mt-3 flex flex-col items-center gap-1 rounded-lg border border-dashed p-6 text-center">
              <p className="text-sm font-medium">{t.overview.pointAtSite}</p>
              <Link to={`/bots/${botId}/sources`} className="text-[12.5px] font-medium text-primary hover:underline">
                {t.overview.addSourceLink}
              </Link>
            </div>
          ) : (
            <>
              <div className="mt-3 flex flex-col text-[13px]">
                <div className="flex items-baseline justify-between gap-2 border-b py-[7px]">
                  <span className="text-muted-foreground">{t.overview.pagesIndexed}</span>
                  <span className="font-semibold">{knowledge.pageCount}</span>
                </div>
                <div className="flex items-baseline justify-between gap-2 border-b py-[7px]">
                  <span className="text-muted-foreground">{t.overview.sourcesCountLabel}</span>
                  <span className="font-semibold">{knowledge.sourceCount}</span>
                </div>
                <div className="flex items-baseline justify-between gap-2 py-[7px]">
                  <span className="text-muted-foreground">{t.overview.lastSyncedLabel}</span>
                  <span className="font-semibold">{relativeTime(knowledge.lastSyncedAt, t.time)}</span>
                </div>
              </div>
              {/* Fix round 2 — this nudge only makes sense once `pageCount` is a settled number;
                  mid-index it could read "Only 0 pages?" beside a "Sources syncing" chip, the
                  same class of overclaim the hero paragraph had. */}
              {knowledgeState === 'ready' && (
                <p className="mt-3 text-[12.5px] leading-normal text-muted-foreground">
                  {t.overview.onlyPagesNudge({ n: knowledge.pageCount })}
                </p>
              )}
              <Link to={`/bots/${botId}/sources`} className="mt-2 text-[12.5px] font-medium text-primary hover:underline">
                {t.overview.addSourcesLink}
              </Link>
            </>
          )}
        </div>
      </div>

      <div className="rounded-[14px] border bg-card p-5 pt-[15px] shadow-el-1">
        <h2 className="font-display text-lg font-semibold">{t.overview.whatHappensNextHeading}</h2>
        <div className="mt-3.5 grid grid-cols-1 gap-4.5 sm:grid-cols-3">
          {nextSteps.map((step) => (
            <div key={step.n} className="flex flex-col gap-1.5">
              <span className="grid h-6 w-6 place-items-center rounded-[7px] bg-muted font-mono text-[11.5px] font-medium text-muted-foreground">
                {step.n}
              </span>
              <span className="text-[13.5px] font-semibold">{step.title}</span>
              <span className="text-pretty text-[12.5px] leading-normal text-muted-foreground">{step.body}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="mb-2 font-mono text-[10.5px] tracking-[0.09em] text-muted-foreground">
          {t.overview.countersHeading}
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[2fr_1fr_1fr]">
          {zeroStats(t).map((stat) => (
            <div key={stat.label} className="flex flex-col gap-1 rounded-[14px] border border-dashed p-4 px-5">
              <span className="text-[13px] text-muted-foreground">{stat.label}</span>
              <span className="font-display text-[28px] leading-[1.1] font-bold text-muted-foreground opacity-50">—</span>
              <span className="text-xs text-muted-foreground">{stat.hint}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
